import React from "react";
import { Typography } from "@mui/material";
import PasswordIcon from "@mui/icons-material/Password";
import PinIcon from "@mui/icons-material/Pin";
import SyncIcon from "@mui/icons-material/Sync";
import LockOpenIcon from "@mui/icons-material/LockOpen";

import PrimaryButton from "../../../widgets/Buttons/PrimaryButton";
import SecondaryButton from "../../../widgets/Buttons/SecondaryButton";
import CustomModal from "../../../widgets/Modal/CustomModal";
import ErrorSnackbar from "../../../widgets/Snackbar/ErrorSnackbar";
import SuccessSnackbar from "../../../widgets/Snackbar/SuccessSnackbar";
import PasswordTextField from "../../../widgets/Textfield/PasswordTextfield";
import useOtpPasswordManagementActions from "../hooks/useOtpPasswordManagementActions";
import styles from "../StudentDetails.module.scss";

const OtpPasswordManagementCard = ({
  phone,
  features,
  studentData,
  verticalInfo,
  branchId,
}) => {
  const {
    otpModal,
    passwordModal,
    confirmModal,
    errorSnackbar,
    successSnackbar,
    setConfirmModal,
    closeOtpModal,
    closePasswordModal,
    closeConfirmModal,
    openPasswordModal,
    updatePasswordValue,
    closeError,
    closeSuccess,
    isUpdatingPassword,
    isGettingOtp,
    isResettingOtp,
    isUnblockingUser,
    handleUpdatePassword,
    handleGetOtp,
    handleConfirmYes,
  } = useOtpPasswordManagementActions({
    phone,
    studentData,
    verticalInfo,
    branchId,
  });

  return (
    <>
      {features.getOtp && (
        <PrimaryButton
          startIcon={<PinIcon />}
          onClick={handleGetOtp}
          isLoading={isGettingOtp}
          className={`${styles.actionButtonLarge} ${styles.actionButtonNeutral}`}
        >
          {isGettingOtp ? "Fetching..." : "Get OTP"}
        </PrimaryButton>
      )}
      {features.resetOtp && (
        <PrimaryButton
          startIcon={<SyncIcon />}
          onClick={() =>
            setConfirmModal({ open: true, actionText: "Reset OTP" })
          }
          className={`${styles.actionButtonLarge} ${styles.actionButtonNeutral}`}
          isLoading={isResettingOtp}
        >
          {isResettingOtp ? "Resetting..." : "Reset OTP"}
        </PrimaryButton>
      )}
      {features.unblock && (
        <PrimaryButton
          startIcon={<LockOpenIcon />}
          onClick={() =>
            setConfirmModal({ open: true, actionText: "Unblock User" })
          }
          className={`${styles.actionButtonLarge} ${styles.actionButtonNeutral}`}
          isLoading={isUnblockingUser}
        >
          {isUnblockingUser ? "Unblocking..." : "Unblock User"}
        </PrimaryButton>
      )}
      {features.updatePassword && (
        <PrimaryButton
          startIcon={<PasswordIcon />}
          onClick={openPasswordModal}
          className={`${styles.actionButtonLarge} ${styles.actionButtonNeutral}`}
          isLoading={isUpdatingPassword}
        >
          {isUpdatingPassword ? "Updating..." : "Update Password"}
        </PrimaryButton>
      )}

      <CustomModal
        open={otpModal.open}
        onClose={closeOtpModal}
        title="Generated OTP"
        maxWidth="xs"
        actions={
          <SecondaryButton onClick={closeOtpModal}>Close</SecondaryButton>
        }
      >
        <div className={styles.otpSurface}>
          <Typography variant="h3" className={styles.otpCode}>
            {otpModal.code}
          </Typography>
        </div>
      </CustomModal>

      <CustomModal
        open={passwordModal.open}
        onClose={closePasswordModal}
        title="Update Password"
        maxWidth="sm"
        actions={
          <>
            <SecondaryButton onClick={closePasswordModal}>
              Cancel
            </SecondaryButton>

            <PrimaryButton
              onClick={handleUpdatePassword}
              isLoading={isUpdatingPassword}
            >
              {isUpdatingPassword ? "Updating..." : "Update Password"}
            </PrimaryButton>
          </>
        }
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleUpdatePassword();
          }}
        >
          <div className={styles.passwordSection}>
            <p className={styles.passwordHint}>
              Password must be 6-64 characters, no spaces, and no consecutive
              dots (<code>..</code>) or underscores (<code>__</code>).
            </p>
            <PasswordTextField
              id="update-password"
              name="password"
              label="New Password"
              autoComplete="new-password"
              value={passwordModal.password}
              error={!!passwordModal.error}
              helperText={passwordModal.error || " "}
              onChange={(e) => updatePasswordValue(e.target.value)}
              fullWidth
            />
          </div>
        </form>
      </CustomModal>

      <CustomModal
        open={confirmModal.open}
        onClose={closeConfirmModal}
        title="Confirm Action"
        maxWidth="xs"
        actions={
          <>
            <SecondaryButton onClick={closeConfirmModal}>
              Cancel
            </SecondaryButton>
            <PrimaryButton onClick={handleConfirmYes}>Confirm</PrimaryButton>
          </>
        }
      >
        <Typography className={styles.modalText}>
          Are you sure you want to proceed with{" "}
          <strong>{confirmModal.actionText}</strong>?
        </Typography>
      </CustomModal>

      <ErrorSnackbar
        open={errorSnackbar.open}
        message={errorSnackbar.message}
        onClose={closeError}
      />
      <SuccessSnackbar
        open={successSnackbar.open}
        message={successSnackbar.message}
        onClose={closeSuccess}
      />
    </>
  );
};

export default OtpPasswordManagementCard;
